import React, { Component } from 'react'
import { Player } from 'video-react'
import Helmet from 'react-helmet'
import WithData from '../components/common/with-data.js'
import { renderComponent } from '../components/common/render-components.js'
import { DivWithBackgroundColor } from '../components/shared-page-components/styled-components.js'
import '../assets/stylesheets/home.scss'
import whiteX from '../assets/images/white-x.svg'

class IndexPage extends Component {
  render() {
    const {
      metaPageTitle,
      metaPageDescription,
      metaImage,
      metaImageTwitter,
      rotatedTitle,
      subtitle,
      homePageHeroImage,
      homePageHeroVideoUrl,
      homePageIntroductionText,
      homePageContentComponents,
      homePageBackgroundColor,
      homePagePrimaryColor,
      homePageSecondaryColor,
    } = this.props

    function addColorsToComponents(component) {
      component.primaryColor = homePagePrimaryColor
      component.secondaryColor = homePageSecondaryColor
    }

    homePageContentComponents.map(component => {
      return addColorsToComponents(component)
    })

    const PageMetaContent = () => (
      <Helmet>
        <title>{metaPageTitle}</title>
        <meta property="og:title" content={metaPageTitle} />
        <meta property="twitter:title" content={metaPageTitle} />
        <meta name="description" content={metaPageDescription} />
        <meta property="og:description" content={metaPageDescription} />
        <meta property="twitter:description" content={metaPageDescription} />
        <meta
          property="og:image"
          content={`${metaImage.file.url}?w=1200&h=630&fit=fill&fm=jpg&q=90`}
        />
        <meta
          property="twitter:image"
          content={`${
            metaImageTwitter.file.url
          }?w=800&h=418&fit=fill&fm=jpg&q=90`}
        />
        <meta name="og:url" content={`${process.env.GATSBY_SITE_BASE_URL}/`} />
      </Helmet>
    )

    return (
      <div id="homepage">
        <PageMetaContent />
        <header className="grid-row">
          <div className="homepage-hero-container">
            <div className="home-page-title-container">
              <h2 className="home-page-title">
                <span
                  dangerouslySetInnerHTML={{
                    __html: subtitle,
                  }}
                />
                <br />
                &darr;
              </h2>
            </div>

            {!homePageHeroVideoUrl ? (
              <div className="homepage-hero-image">
                <img src={`${homePageHeroImage}`} />
              </div>
            ) : (
              <div className="homepage-hero-video-container">
                <div className="homepage-hero-video">
                  <div className="video-cover" />
                  <Player
                    ref="player"
                    muted
                    loop
                    autoPlay
                    playsInline
                    className="hero-video"
                  >
                    <source src={homePageHeroVideoUrl} />
                  </Player>
                  <img className="poster-image" src={homePageHeroImage} />
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="futures-container" />

        <DivWithBackgroundColor backgroundColor={homePageBackgroundColor}>
          <div className="main-container">
            {homePageIntroductionText && (
              <section className="homepage-introduction grid-row">
                <div className="section-column">
                  <p>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: homePageIntroductionText,
                      }}
                    />
                  </p>
                  )}
                </div>
              </section>
            )}

            {homePageContentComponents.map((component, i) => {
              return renderComponent(component, i)
            })}
          </div>
        </DivWithBackgroundColor>
      </div>
    )
  }
}

export default WithData(mapDataToProps, IndexPage)

function mapDataToProps(data) {
  const page = data.contentfulHomePage

  return {
    title: page.title || '',
    metaPageTitle: page.metaPageTitle || '',
    metaPageDescription: page.metaPageDescription.metaPageDescription || '',
    metaImage: page.metaImage ? page.metaImage : { file: { url: '' } },
    metaImageTwitter: page.metaImageTwitter
      ? page.metaImageTwitter
      : { file: { url: '' } },
    rotatedTitle: page.rotatedTitle || '',
    subtitle: page.subtitle || '',
    homePageHeroImage: page.homePageHeroImage
      ? page.homePageHeroImage.file.url
      : '',
    homePageHeroVideoUrl: page.homePageHeroVideoUrl
      ? page.homePageHeroVideoUrl
      : '',
    homePageIntroductionText: page.homePageIntroductionText
      ? page.homePageIntroductionText.homePageIntroductionText
      : '',
    homePageContentComponents: page.homePageContentComponents
      ? page.homePageContentComponents
      : [{}],
    homePageBackgroundColor: page.homePageBackgroundColor
      ? page.homePageBackgroundColor
      : '',
    homePagePrimaryColor: page.homePagePrimaryColor
      ? page.homePagePrimaryColor
      : '',
    homePageSecondaryColor: page.homePageSecondaryColor
      ? page.homePageSecondaryColor
      : '',
  }
}

export const query = graphql`
  query indexQuery {
    contentfulHomePage(title: { eq: "Home Page - Master" }) {
      title
      metaPageTitle
      metaPageDescription {
        metaPageDescription
      }
      metaImage {
        file {
          url
        }
      }
      metaImageTwitter {
        file {
          url
        }
      }
      rotatedTitle
      subtitle
      homePageHeroImage {
        file {
          url
        }
      }
      homePageHeroVideoUrl
      homePageIntroductionText {
        homePageIntroductionText
      }
      homePageContentComponents {
        ...allHomePageImageBlocks
        ...allHomePageHalfImageBlocks
        ...allHomePageCarousels
        ...allHomePageQuotes
        ...allHomePageTextBlocks
        ...allHomePageVideosClickToPlay
        ...allHomePageVideosMutedAutoplay
        ...allHomePageFeatureTwoImageBlock
        ...allHomePageFeatureImageBlocks
        ...allHomePageFeatureHalfImageBlocks
      }
      homePageBackgroundColor
      homePagePrimaryColor
      homePageSecondaryColor
    }
  }

  fragment allHomePageImageBlocks on ContentfulComponentImageBlock {
    __typename
    title
    image {
      file {
        url
      }
    }
    imageContentColumnWidth
    imageShiftContent
    captionTitle
    captionCopy {
      captionCopy
    }
    captionContentColumnWidth
    captionShiftContent
    marginBelow
  }

  fragment allHomePageHalfImageBlocks on ContentfulComponentHalfImageBlock {
    __typename
    title
    image {
      file {
        url
      }
    }
    imageAlign
    doesImageTakesFullWidthOnMobile
    captionTitle
    captionCopy {
      captionCopy
    }
    captionAlign
    marginBelow
  }

  fragment allHomePageCarousels on ContentfulComponentCarousel {
    __typename
    slides {
      title
      image {
        file {
          url
        }
      }
      captionTitle
      captionCopy {
        captionCopy
      }
    }
    marginBelow
  }

  fragment allHomePageQuotes on ContentfulComponentQuote {
    __typename
    title
    circleCroppedImage {
      file {
        url
      }
    }
    captionCopy {
      captionCopy
    }
    quoteAuthorOrSocialHandle
    contentColumnWidth
    shiftContent
    marginBelow
    linkToAuthorOrSocialMedia
  }

  fragment allHomePageTextBlocks on ContentfulComponentTextBlock {
    __typename
    title
    blockTitle
    blockCopy {
      blockCopy
    }
    textColor
    contentColumnWidth
    shiftContent
    marginBelow
  }

  fragment allHomePageVideosClickToPlay on ContentfulComponentVideoClickToPlay {
    __typename
    title
    vimeoUrl
    posterOverlayColor
    columnWidth
    alignment
    captionTitle
    captionAlignment
    marginBelow
    vimeoPosterImage {
      file {
        url
      }
    }
    captionCopy {
      captionCopy
    }
  }

  fragment allHomePageVideosMutedAutoplay on ContentfulComponentVideoMutedAutoplay {
    __typename
    title
    vimeoUrl
    columnWidth
    alignment
    captionTitle
    captionAlignment
    marginBelow
    vimeoPosterImage {
      file {
        url
      }
    }
    captionCopy {
      captionCopy
    }
  }

  fragment allHomePageFeatureTwoImageBlock on ContentfulFeatureComponentTwoImageBlock {
    __typename
    title
    leftImage {
      file {
        url
      }
    }
    leftImageExternalLink
    leftImageCaseStudyReference {
      slug
    }
    leftCaptionTitle
    leftImageOverlayColor
    leftCaptionCopy {
      leftCaptionCopy
    }
    rightImage {
      file {
        url
      }
    }
    rightImageExternalLink
    rightImageCaseStudyReference {
      slug
    }
    rightImageOverlayColor
    rightCaptionTitle
    rightCaptionCopy {
      rightCaptionCopy
    }
    marginBelow
  }

  fragment allHomePageFeatureImageBlocks on ContentfulFeatureComponentImageBlock {
    __typename
    title
    image {
      file {
        url
      }
    }
    imageContentColumnWidth
    imageShiftContent
    captionTitle
    captionCopy {
      captionCopy
    }
    captionContentColumnWidth
    captionShiftContent
    marginBelow
    imageOverlayColor
    imageCaseStudyReference {
      slug
    }
    imageExternalLink
  }

  fragment allHomePageFeatureHalfImageBlocks on ContentfulFeatureComponentHalfImageBlock {
    __typename
    title
    image {
      file {
        url
      }
    }
    imageAlign
    doesImageTakesFullWidthOnMobile
    captionTitle
    captionCopy {
      captionCopy
    }
    captionAlign
    marginBelow
    imageOverlayColor
    imageCaseStudyReference {
      slug
    }
    imageExternalLink
  }
`



// WEBPACK FOOTER //
// ./src/pages/index.js